// Firebase Analytics, under the cookie banner's one answer (#458, #755).
//
// Wired by the rules in docs/operations.md §5a. What may be sent is
// src/lib/analyticsEvents.js; this file is *when*.
//
// ── Opt-out: on unless declined (#755) ─────────────────────────────────────
//
// `getConsent()` is a grant unless the visitor declined or the browser sends
// Global Privacy Control (marketing/consent.js), so a visitor who has not
// answered is measured and the banner is a notice. Nothing in the app imports
// the SDK: `installAnalytics` is handed a loader (the SDK's dynamic import, in
// src/main.jsx) and calls it only while `getConsent()` says 'granted' — at
// startup, or when "OK" / "Allow" is pressed after a Decline. While it says
// 'denied' no gtag.js is fetched, no Firebase Installations id is made and no
// cookie is set. With no measurement id configured
// (`VITE_FIREBASE_MEASUREMENT_ID`, src/lib/firebase.js) it is never called.
//
// An event recorded while denied is dropped, not kept: holding it would be
// collecting it. Events recorded while granted, with the SDK still loading,
// are held — a few, briefly — and sent once it is up, unless the answer has
// changed meanwhile.
//
// ── What the SDK is told ───────────────────────────────────────────────────
//
// Consent Mode v2 before initialisation, as the defaults gtag.js starts from:
// the state of a grant (`CONSENT_MODE.granted`, marketing/consent.js), because
// the SDK is only started while one is in force. Not denied-then-update: this
// SDK pushes an update at once but its defaults only after an await, so the
// update would reach gtag.js before the default. A grant is
// `analytics_storage`, `ad_storage` and `ad_user_data` (#754);
// `ad_personalization` stays denied, since the grant covers measurement, not
// remarketing. The Ads tag is src/lib/adConversions.js; this SDK sends none
// of its events.
//
// The configuration turns off Google signals and ad personalisation, sends no
// automatic page view (the router sends its own, with the route's pattern for
// the address — analyticsEvents.js `pageViewParams`), sets the location and
// referrer to those clean forms, and keeps GA's cookies for a year rather than
// two, so they last no longer than the answer that allowed them. No user id is
// set.
//
// ── A Decline turns it off in the same page ────────────────────────────────
//
// `onConsentChange('denied')`: every signal back to denied, collection
// disabled (gtag.js's `ga-disable-<id>` switch), the Firebase Installations id
// deleted — on Firebase's server as well as here — and GA's `_ga` cookies
// expired on every domain they could be on, with the Google Ads `_gcl_*`
// cookies, since a grant let gtag.js write those too. gtag.js stays in the
// page, inert, because a script cannot be unloaded; no reload is needed.
// Pressing "Allow" in the same page turns collection back on.
//
// A browser that arrives denied but still holding GA cookies — the answer
// withdrawn in another tab or on the marketing site, or GPC switched on since
// — has them removed at startup, with the Installations database.
//
// ── Never in the way ───────────────────────────────────────────────────────
//
// Nothing here throws into a caller or rejects: `trackEvent` is called from
// the data layer after a write, and a failure to count it must not look like
// a failure of the write. Ad blockers make the SDK fail to load for some
// visitors; that is expected, not an error.

import { CONSENT_MODE, getConsent, onConsentChange } from './consent';
import { loadCore } from './core.js';
import {
  ANALYTICS, cleanAnalyticsEvent, gaCookieDeletions, gaCookieNames, pageViewParams, referrerOrigin,
} from './analyticsEvents';
import { adCookieDeletions, adCookieNames } from '../../marketing/adTag.js';

// The event names, so a caller imports one module.
export { ANALYTICS };

// The IndexedDB database Firebase Installations keeps its id in. Removed
// directly only when the SDK is not running in this page; when it is, the
// SDK's own `deleteInstallations` does it and tells the server.
const INSTALLATIONS_DB = 'firebase-installations-database';

const HOLD_LIMIT = 20;
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

// The consent state gtag.js starts from: a grant's. See the header for why.
export const CONSENT_AT_START = CONSENT_MODE.granted;

let wiring = null;
// 'off' | 'loading' | 'on' | 'unsupported'
let phase = 'off';
let sdk = null;
let instance = null;
let held = [];
let currentPath = null;
let stopListening = null;

const origin = () => (typeof location === 'undefined' ? '' : location.origin);

/** The configuration `initializeAnalytics` is given. Exported for the test. */
export function analyticsConfig(pathname, referrer) {
  const page = pageViewParams(pathname ?? '/', origin());
  return {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_expires: ONE_YEAR_SECONDS,
    page_location: page.page_location,
    page_title: page.page_title,
    page_referrer: referrerOrigin(referrer),
  };
}

/**
 * Wire analytics to the consent answer. Called once, from src/main.jsx.
 *
 * `app` is the Firebase app, `measurementId` the GA4 stream's id (none: the
 * call is a no-op, and nothing ever loads), `loadAnalytics` and
 * `loadInstallations` the dynamic imports of `firebase/analytics` and
 * `firebase/installations`.
 * @returns {boolean} whether analytics is wired at all
 */
export function installAnalytics({ app, measurementId, loadAnalytics, loadInstallations } = {}) {
  if (getConsent() !== 'granted') clearLeftovers();
  if (wiring || !app || !measurementId || typeof loadAnalytics !== 'function') return false;
  wiring = { app, loadAnalytics, loadInstallations };
  stopListening = onConsentChange((choice) => {
    if (choice === 'granted') start();
    else withdraw();
  });
  if (getConsent() === 'granted') start();
  return true;
}

/**
 * Record one of the moments in analyticsEvents.js. Sent when analytics is on,
 * held while it is loading under a grant, dropped otherwise. Never throws.
 * @returns {boolean} whether it was sent or held
 */
export function trackEvent(name, params) {
  try {
    const clean = cleanAnalyticsEvent(name, params);
    if (!clean.ok) return false;
    if (phase === 'on') return send(clean);
    if (phase === 'loading' && held.length < HOLD_LIMIT && getConsent() === 'granted') {
      held.push(clean);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * A page view, from the router: `pathname` is the router's, without the base
 * path. Remembered whether or not it is sent, so that a grant made on this
 * screen can count the view of it.
 */
export function trackPageView(pathname) {
  currentPath = pathname ?? '/';
  try {
    return trackEvent(ANALYTICS.pageView, pageViewParams(currentPath, origin()));
  } catch {
    // The pattern is the core's (#557); a view the core cannot yet answer
    // for is not counted, and never breaks the screen it was for.
    return false;
  }
}

/** Forget everything, for tests. */
export function resetAnalyticsForTests() {
  stopListening?.();
  stopListening = null;
  wiring = null;
  phase = 'off';
  sdk = null;
  instance = null;
  held = [];
  currentPath = null;
}

/** Where the module is, for tests. */
export const analyticsPhase = () => phase;

function send({ name, params }) {
  try {
    // gtag.js stamps the location on every event, the automatic ones too; it
    // is set to the clean form whenever the page changes so that none of them
    // carries the real address. (Reasoned from gtag.js's documented `set`, not
    // observed in DebugView.)
    if (name === ANALYTICS.pageView) {
      sdk.setDefaultEventParameters({ page_location: params.page_location, page_title: params.page_title });
    }
    sdk.logEvent(instance, name, params);
    return true;
  } catch {
    return false;
  }
}

async function start() {
  if (!wiring || phase === 'loading' || phase === 'unsupported') return;
  if (instance) {
    // Allowed again after a Decline in this same page.
    try {
      sdk.setConsent({ ...CONSENT_MODE.granted });
      sdk.setAnalyticsCollectionEnabled(instance, true);
      phase = 'on';
      if (currentPath != null) trackPageView(currentPath);
    } catch {
      phase = 'off';
    }
    return;
  }
  phase = 'loading';
  try {
    const mod = await wiring.loadAnalytics();
    // The list and the page patterns are the core's (#557), and a grant read
    // at startup can get here before src/main.jsx's own wait for it is over.
    await loadCore();
    if (!(await mod.isSupported())) {
      phase = 'unsupported';
      held = [];
      return;
    }
    // Declined while it was loading: stop here, before anything is stored.
    if (getConsent() !== 'granted') {
      phase = 'off';
      held = [];
      return;
    }
    sdk = mod;
    sdk.setConsent({ ...CONSENT_AT_START });
    instance = sdk.initializeAnalytics(wiring.app, {
      config: analyticsConfig(currentPath, typeof document === 'undefined' ? '' : document.referrer),
    });
    phase = 'on';
    const waiting = held;
    held = [];
    // The screen the grant was made on counts as viewed, unless its view is
    // already among the held events.
    if (currentPath != null && !waiting.some((e) => e.name === ANALYTICS.pageView)) {
      send(cleanAnalyticsEvent(ANALYTICS.pageView, pageViewParams(currentPath, origin())));
    }
    for (const e of waiting) send(e);
  } catch {
    // Blocked, offline, or refused by the SDK: no analytics in this page.
    phase = 'off';
    held = [];
  }
}

function withdraw() {
  held = [];
  // A load in flight is left to finish: it reads the answer again before it
  // initialises anything, and stops there. Starting a second one on a quick
  // Allow would race it to initialise.
  if (phase === 'on') phase = 'off';
  if (sdk && instance) {
    try {
      sdk.setConsent({ ...CONSENT_MODE.denied });
      sdk.setAnalyticsCollectionEnabled(instance, false);
    } catch {
      // Collection is already off if either throws: nothing was initialised.
    }
    deleteInstallation();
    expireGaCookies();
  } else {
    clearLeftovers();
  }
}

function deleteInstallation() {
  const load = wiring?.loadInstallations;
  if (typeof load !== 'function') return;
  Promise.resolve()
    .then(load)
    .then((m) => m.deleteInstallations(m.getInstallations(wiring.app)))
    .then(null, () => {});
}

function expireGaCookies() {
  if (typeof document === 'undefined' || typeof location === 'undefined') return false;
  const deletions = [
    ...gaCookieDeletions(document.cookie, location.hostname),
    ...adCookieDeletions(document.cookie, location.hostname),
  ];
  for (const c of deletions) document.cookie = c;
  return deletions.length > 0;
}

// GA cookies in a browser that is denied: analytics ran here once, so its
// cookies and its Installations id are removed, and any Ads cookies beside
// them. Without them there is nothing to do, and the database is not touched.
function clearLeftovers() {
  if (typeof document === 'undefined') return;
  if (!gaCookieNames(document.cookie).length && !adCookieNames(document.cookie).length) return;
  expireGaCookies();
  try {
    globalThis.indexedDB?.deleteDatabase(INSTALLATIONS_DB);
  } catch {
    // Storage blocked: nothing could have been written there either.
  }
}
